/*
 * hardware licensing service
 * manages license requests and validation
 */

import { supabase } from './supabase'
import { getHardwareFingerprint, getCurrentIP } from './hardwareId'

export interface LicenseRequest {
  id: string
  user_id: string
  user_email: string
  hardware_id: string
  machine_name: string | null
  device_name: string | null
  request_ip: string | null
  status: 'pending' | 'approved' | 'rejected'
  requested_at: string
  reviewed_at: string | null
  reviewed_by: string | null
  rejection_reason: string | null
  notes: string | null
}

export interface WhitelistedHardware {
  id: string
  user_id: string
  user_email: string
  hardware_id: string
  machine_name: string | null
  device_name: string | null
  is_active: boolean
  approved_by: string | null
  approved_at: string
  last_seen_at: string | null
  last_seen_ip: string | null
  notes: string | null
}

export interface LicenseStatus {
  isLicensed: boolean
  hardwareId: string | null
  message: string
  currentHardwareId?: string
}

/* normalize hardware id to uppercase for case-insensitive comparison */
function normalizeHardwareId(hardwareId: string): string {
  return hardwareId.toUpperCase()
}

/* check if current user has a valid license */
export async function checkLicense(): Promise<LicenseStatus> {
  try {
    const fingerprint = await getHardwareFingerprint()
    // normalize hardware id to uppercase for consistency
    const normalizedHardwareId = normalizeHardwareId(fingerprint.hardwareId)
    
    const { data, error } = await supabase
      .rpc('check_user_license', {
        p_hardware_id: normalizedHardwareId // pass normalized id to rpc
      })

    if (error) {
      console.error('Error checking license:', error)
      return {
        isLicensed: false,
        hardwareId: null,
        message: 'Error checking license',
        currentHardwareId: normalizedHardwareId
      }
    }

    // RPC returns rows[] when multi-column, or scalar/jsonb when single-column.
    const row: any = Array.isArray(data) ? data[0] : data

    return {
      isLicensed: row?.is_licensed || false,
      hardwareId: row?.hardware_id || null,
      message: row?.message || 'Unknown status',
      currentHardwareId: normalizedHardwareId
    }
  } catch (error) {
    console.error('Error checking license:', error)
    return {
      isLicensed: false,
      hardwareId: null,
      message: 'Error checking license'
    }
  }
}

/*
 * request a license for specified hardware id
 * @param hardwareid - the hardware id to request license for (user must paste this)
 */
export async function requestLicense(hardwareId: string): Promise<{ success: boolean; message: string; requestId?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    
    if (!user) {
      return { success: false, message: 'Not authenticated' }
    }

    if (!hardwareId || hardwareId.trim().length === 0) {
      return { success: false, message: 'Hardware ID is required' }
    }

    const fingerprint = await getHardwareFingerprint()
    const ip = await getCurrentIP()

    // normalize hardware id to uppercase for case-insensitive comparison
    const normalizedHardwareId = normalizeHardwareId(hardwareId.trim())
    
    // check if request already exists (case-insensitive)
    const { data: existing } = await supabase
      .from('license_requests')
      .select('*')
      .eq('user_id', user.id)
      .ilike('hardware_id', normalizedHardwareId) // case-insensitive comparison
      .eq('status', 'pending')
      .single()

    if (existing) {
      return { 
        success: false, 
        message: 'You already have a pending license request for this machine' 
      }
    }

    // check if already whitelisted (case-insensitive)
    const { data: whitelisted } = await supabase
      .from('whitelisted_hardware')
      .select('*')
      .eq('user_id', user.id)
      .ilike('hardware_id', normalizedHardwareId) // case-insensitive comparison
      .eq('is_active', true)
      .single()

    if (whitelisted) {
      return { 
        success: false, 
        message: 'This machine is already licensed' 
      }
    }

    // create license request (store as uppercase for consistency)
    const { data: request, error } = await supabase
      .from('license_requests')
      .insert([{
        user_id: user.id,
        user_email: user.email!,
        hardware_id: normalizedHardwareId, // store as uppercase
        machine_name: fingerprint.machineName,
        device_name: fingerprint.deviceName,
        request_ip: ip
      }])
      .select()
      .single()

    if (error) throw error

    return { 
      success: true, 
      message: 'License request submitted successfully. Waiting for admin approval.',
      requestId: request.id
    }
  } catch (error) {
    console.error('Error requesting license:', error)
    return { 
      success: false, 
      message: 'Failed to submit license request' 
    }
  }
}

/* get user's license requests */
export async function getUserLicenseRequests(): Promise<LicenseRequest[]> {
  try {
    const { data, error } = await supabase
      .from('license_requests')
      .select('*')
      .order('requested_at', { ascending: false })

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error fetching license requests:', error)
    return []
  }
}

/* get user's whitelisted hardware */
export async function getUserWhitelistedHardware(): Promise<WhitelistedHardware[]> {
  try {
    const { data, error } = await supabase
      .from('whitelisted_hardware')
      .select('*')
      .order('approved_at', { ascending: false })

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error fetching whitelisted hardware:', error)
    return []
  }
}

/* update last seen info for whitelisted hardware */
export async function updateLastSeen(hardwareId: string): Promise<void> {
  try {
    const ip = await getCurrentIP()
    // normalize to uppercase for case-insensitive comparison
    const normalizedHardwareId = normalizeHardwareId(hardwareId)
    
    await supabase
      .from('whitelisted_hardware')
      .update({
        last_seen_at: new Date().toISOString(),
        last_seen_ip: ip
      })
      .ilike('hardware_id', normalizedHardwareId) // case-insensitive comparison
  } catch (error) {
    console.error('Error updating last seen:', error)
  }
}

/* admin: get all pending license requests */
export async function getPendingLicenseRequests(): Promise<LicenseRequest[]> {
  try {
    const { data, error } = await supabase
      .from('license_requests')
      .select('*')
      .eq('status', 'pending')
      .order('requested_at', { ascending: true })

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error fetching pending requests:', error)
    return []
  }
}

/* admin: get all whitelisted hardware */
export async function getAllWhitelistedHardware(): Promise<WhitelistedHardware[]> {
  try {
    const { data, error } = await supabase
      .from('whitelisted_hardware')
      .select('*')
      .order('approved_at', { ascending: false })

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error fetching whitelisted hardware:', error)
    return []
  }
}

/* unwrap an RPC TABLE(success, message) result */
function unwrapRpcResult(data: any, fallbackMsg: string): { success: boolean; message: string } {
  const row = Array.isArray(data) ? data[0] : data
  if (row && typeof row.success === 'boolean') {
    return { success: row.success, message: row.message ?? fallbackMsg }
  }
  return { success: true, message: fallbackMsg }
}

/* admin: approve license request */
export async function approveLicenseRequest(requestId: string, notes?: string): Promise<{ success: boolean; message: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    const { data, error } = await supabase
      .rpc('approve_license_request', {
        p_request_id: requestId,
        p_notes: notes || null,
        p_admin_user_id: user?.id ?? null,
      })

    if (error) throw error

    return unwrapRpcResult(data, 'License approved successfully')
  } catch (error: any) {
    console.error('Error approving license:', error)
    return {
      success: false,
      message: error.message || 'Failed to approve license'
    }
  }
}

/* admin: reject license request */
export async function rejectLicenseRequest(requestId: string, reason: string): Promise<{ success: boolean; message: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    const { data, error } = await supabase
      .rpc('reject_license_request', {
        p_request_id: requestId,
        p_reason: reason,
        p_admin_user_id: user?.id ?? null,
      })

    if (error) throw error

    return unwrapRpcResult(data, 'License rejected')
  } catch (error: any) {
    console.error('Error rejecting license:', error)
    return {
      success: false,
      message: error.message || 'Failed to reject license'
    }
  }
}

/* admin: update notes on a whitelisted hardware row */
export async function updateHardwareNotes(
  hardwareId: string,
  notes: string | null
): Promise<{ success: boolean; message: string }> {
  try {
    const { error } = await supabase
      .from('whitelisted_hardware')
      .update({ notes })
      .eq('id', hardwareId)
    if (error) throw error
    return { success: true, message: 'Notes updated' }
  } catch (error: any) {
    console.error('Error updating hardware notes:', error)
    return { success: false, message: error.message || 'Failed to update notes' }
  }
}

/* admin: deactivate whitelisted hardware */
export async function deactivateHardware(hardwareId: string): Promise<{ success: boolean; message: string }> {
  try {
    const { error } = await supabase
      .from('whitelisted_hardware')
      .update({ is_active: false })
      .eq('id', hardwareId)

    if (error) throw error

    return { 
      success: true, 
      message: 'Hardware deactivated successfully'
    }
  } catch (error: any) {
    console.error('Error deactivating hardware:', error)
    return { 
      success: false, 
      message: error.message || 'Failed to deactivate hardware'
    }
  }
}

